import { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EventFormData } from '../components/EventDialog';
import {
  GET_CALENDAR_DATA,
  CREATE_EVENT,
  UPDATE_EVENT,
  DELETE_EVENT
} from '../queries';

export const useCalendarHandlers = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [dialogInitialData, setInitialData] = useState<Partial<EventFormData> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedRange, setSelectedRange] = useState({
    start: dayjs().startOf('month').subtract(1, 'week').toISOString(),
    end: dayjs().endOf('month').add(1, 'week').toISOString(),
  });

  const { data, loading, error, refetch } = useQuery(GET_CALENDAR_DATA, {
    variables: { dateRange: selectedRange },
    fetchPolicy: 'cache-and-network'
  });

  const [createEvent] = useMutation(CREATE_EVENT, { onCompleted: () => refetch() });
  const [updateEvent] = useMutation(UPDATE_EVENT, { onCompleted: () => refetch() });
  const [deleteEvent] = useMutation(DELETE_EVENT, { onCompleted: () => refetch() });

  const events = useMemo(() => {
    return data?.getCalendarEvents.map((e: any) => {
      const start = dayjs(isNaN(Number(e.startTime)) ? e.startTime : Number(e.startTime));
      const end = dayjs(isNaN(Number(e.endTime)) ? e.endTime : Number(e.endTime));

      const startStr = e.allDay
        ? (e.startTime.includes('T') ? e.startTime.split('T')[0] : e.startTime)
        : start.toISOString();
      const endStr = e.allDay
        ? (e.endTime.includes('T') ? e.endTime.split('T')[0] : e.endTime)
        : end.toISOString();

      return {
        id: e.id,
        title: e.title || 'Untitled',
        start: startStr,
        end: endStr,
        backgroundColor: e.color || theme.palette.primary.main,
        borderColor: e.color || theme.palette.primary.main,
        display: 'block',
        allDay: e.allDay || false,
        extendedProps: { ...e }
      };
    }) || [];
  }, [data, theme.palette.primary.main]);

  const handleDateClick = useCallback((info: any) => {
    if (info.view.type === 'dayGridMonth') {
      navigate(`/day-view/${info.dateStr}`);
    }
  }, [navigate]);

  const handleEventClick = useCallback((info: any) => {
    info.jsEvent.stopPropagation();
    setSelectedEventId(info.event.id);
    setInitialData(null);
    setIsDialogOpen(true);
  }, []);

  const handleSelect = useCallback((info: any) => {
    if (info.view.type !== 'dayGridMonth') {
      setSelectedEventId(null);
      setInitialData({
        startTime: dayjs(info.start),
        endTime: dayjs(info.end),
        repeat: 'never'
      });
      setIsDialogOpen(true);
    }
  }, []);

  const handleCreateOrUpdate = (formData: EventFormData) => {
    const eventInput = {
      title: formData.title,
      description: formData.description,
      startTime: formData.startTime.toISOString(),
      endTime: formData.endTime.toISOString(),
      location: formData.location,
      color: formData.color,
      allDay: false,
      repeat: formData.repeat,
      reminders: []
    };

    const baseId = selectedEventId?.split('_')[0];

    if (baseId) {
      updateEvent({ variables: { id: baseId, event: eventInput } });
    } else {
      createEvent({ variables: { event: eventInput } });
    }
  };

  const handleDelete = () => {
    const baseId = selectedEventId?.split('_')[0];
    if (!baseId) return;
    setIsDialogOpen(false);
    setDeleteDialogOpen(false);
    deleteEvent({ variables: { id: baseId } });
  };

  const handleDatesSet = useCallback((dateInfo: any) => {
    const newRange = {
      start: dateInfo.startStr,
      end: dateInfo.endStr
    };
    setSelectedRange(prev => {
      if (prev.start === newRange.start && prev.end === newRange.end) return prev;
      return newRange;
    });
  }, []);

  const selectedEvent = useMemo(() =>
    data?.getCalendarEvents.find((e: any) => e.id === selectedEventId),
    [data, selectedEventId]
  );

  return {
    isDialogOpen, setIsDialogOpen,
    selectedEventId, setSelectedEventId,
    dialogInitialData, setInitialData,
    deleteDialogOpen, setDeleteDialogOpen,
    events, loading, error,
    selectedEvent,
    handleDateClick,
    handleEventClick,
    handleSelect,
    handleCreateOrUpdate,
    handleDelete,
    handleDatesSet,
  };
};
